import { Guides, SimpleRect } from './PdfFormEnhancement';

/**
 * Extract snap guides from a PDF-converted SVG by reading vector geometry
 * (axis-aligned path/line/rect segments), not element bounding boxes.
 *
 * A stroke (up to STROKE_CENTER_MAX) contributes one centerline, and copies
 * of that stroke a couple of pixels apart collapse into it. A short cell is
 * thicker than a stroke, so it keeps its top and bottom edges instead of a
 * line through the middle. A larger filled rectangle still contributes both edges.
 */

const AA_EPS = 0.85;
const AA_SLOPE = 0.02;
const CLUSTER_EPS = 0.85;
const CLUSTER_MAX_SPAN = 1.25;
const MIN_SEG_LEN_PX = 18;
const MIN_SEG_LEN_RATIO = 0.02;
const MAX_RULE_THICKNESS = 25;
/** Heavier than this is a cell edge, not a stroke, so keep both sides. */
const STROKE_CENTER_MAX = 8;
const MAX_CELL_AREA_RATIO = 0.22;
const MERGE_GAP = 10;
const GLYPH_CURVE_LIMIT = 6;
/** Collapse stroke-edge duplicates that sit a few pixels apart. */
const GUIDE_COLLAPSE_PX = 2;

type Mat = { a: number; b: number; c: number; d: number; e: number; f: number };
type Pt = { x: number; y: number };

export type AxisSegment = {
  axis: 'h' | 'v';
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

export type WeightedGuide = { value: number; weight: number };

const IDENTITY: Mat = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

const multiply = (m1: Mat, m2: Mat): Mat => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f,
});

const applyMat = (m: Mat, x: number, y: number): Pt => ({
  x: m.a * x + m.c * y + m.e,
  y: m.b * x + m.d * y + m.f,
});

const parseNums = (raw: string): number[] =>
  (raw.match(/[+-]?(?:\d*\.\d+|\d+\.?)(?:[eE][+-]?\d+)?/g) || []).map(Number);

function parseTransformAttr(attr: string | null): Mat {
  if (!attr || !attr.trim() || attr === 'none') return IDENTITY;
  let m = IDENTITY;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(attr))) {
    const kind = match[1].toLowerCase();
    const n = parseNums(match[2]);
    let t = IDENTITY;
    if (kind === 'matrix' && n.length >= 6) {
      t = { a: n[0], b: n[1], c: n[2], d: n[3], e: n[4], f: n[5] };
    } else if (kind === 'translate') {
      t = { a: 1, b: 0, c: 0, d: 1, e: n[0] || 0, f: n[1] || 0 };
    } else if (kind === 'scale') {
      const sx = n[0] ?? 1;
      const sy = n[1] ?? sx;
      t = { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
    } else if (kind === 'rotate') {
      const ang = ((n[0] || 0) * Math.PI) / 180;
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const cx = n[1] || 0;
      const cy = n[2] || 0;
      t = multiply(
        { a: 1, b: 0, c: 0, d: 1, e: cx, f: cy },
        multiply({ a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 }, { a: 1, b: 0, c: 0, d: 1, e: -cx, f: -cy })
      );
    } else if (kind === 'skewx') {
      t = { a: 1, b: 0, c: Math.tan(((n[0] || 0) * Math.PI) / 180), d: 1, e: 0, f: 0 };
    } else if (kind === 'skewy') {
      t = { a: 1, b: Math.tan(((n[0] || 0) * Math.PI) / 180), c: 0, d: 1, e: 0, f: 0 };
    }
    m = multiply(t, m);
  }
  return m;
}

function viewBoxMatrix(svg: SVGSVGElement): Mat {
  let vbX = 0;
  let vbY = 0;
  let vbW = 0;
  let vbH = 0;
  try {
    const vb = svg.viewBox?.baseVal;
    if (vb && vb.width && vb.height) {
      vbX = vb.x;
      vbY = vb.y;
      vbW = vb.width;
      vbH = vb.height;
    }
  } catch {}
  if (!vbW || !vbH) {
    const attr = svg.getAttribute('viewBox');
    if (attr) {
      const p = attr.trim().split(/[\s,]+/).map(Number);
      if (p.length >= 4 && p[2] > 0 && p[3] > 0) {
        vbX = p[0];
        vbY = p[1];
        vbW = p[2];
        vbH = p[3];
      }
    }
  }
  const portW =
    svg.clientWidth ||
    parseFloat(String(svg.getAttribute('width') || '').replace('px', '')) ||
    vbW ||
    1;
  const portH =
    svg.clientHeight ||
    parseFloat(String(svg.getAttribute('height') || '').replace('px', '')) ||
    vbH ||
    1;
  if (!vbW || !vbH) return IDENTITY;
  const sx = portW / vbW;
  const sy = portH / vbH;
  return { a: sx, b: 0, c: 0, d: sy, e: -vbX * sx, f: -vbY * sy };
}

function matrixFromSvgTree(el: Element, svg: SVGSVGElement): Mat {
  let m = IDENTITY;
  let n: Element | null = el;
  while (n) {
    m = multiply(parseTransformAttr(n.getAttribute('transform')), m);
    if (n === svg) break;
    n = n.parentElement;
  }
  return multiply(viewBoxMatrix(svg), m);
}

function readCssScale(el: HTMLElement): number {
  try {
    const tr = getComputedStyle(el).transform;
    if (!tr || tr === 'none') return 1;
    if (tr.startsWith('matrix3d(')) {
      const nums = tr.slice(9, -1).split(',').map(Number);
      const a = nums[0];
      const d = nums[5];
      return !isNaN(a) ? a : (!isNaN(d) ? d : 1);
    }
    if (tr.startsWith('matrix(')) {
      const nums = tr.slice(7, -1).split(',').map(Number);
      const a = nums[0];
      const d = nums[3];
      return !isNaN(a) ? a : (!isNaN(d) ? d : 1);
    }
  } catch {}
  return 1;
}

function isPaintNone(value: string | null | undefined): boolean {
  const s = String(value || '').trim().toLowerCase();
  return !s || s === 'none' || s === 'transparent' || s === 'rgba(0, 0, 0, 0)';
}

function attrOrCss(el: Element, cssName: string, attrName: string): string {
  try {
    const cs = getComputedStyle(el as Element);
    const v = (cs as any)[cssName];
    if (v) return String(v);
  } catch {}
  return el.getAttribute(attrName) || '';
}

function strokeWidthLocal(el: Element): number {
  const raw = attrOrCss(el, 'strokeWidth', 'stroke-width');
  const n = parseFloat(raw);
  if (!isFinite(n) || n <= 0) return 1;
  return n;
}

function numAttr(el: Element, name: string, fallback = 0): number {
  const v = el.getAttribute(name);
  if (v == null || v === '') return fallback;
  const n = parseFloat(v);
  return isFinite(n) ? n : fallback;
}

function shouldSkipElement(el: Element): boolean {
  try {
    if (el.closest('defs, symbol, clipPath, mask, pattern, marker')) return true;
  } catch {}
  try {
    if (el.closest('[id*="logo" i], [class*="logo" i]')) return true;
  } catch {}
  try {
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') === 0) return true;
  } catch {}
  return false;
}

function looksLikeGlyphOutline(d: string): boolean {
  const curves = (d.match(/[CcSsQqTtAa]/g) || []).length;
  const lines = (d.match(/[LlHhVv]/g) || []).length;
  return curves >= GLYPH_CURVE_LIMIT && curves > lines * 2;
}

function pointLineDist(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-8) return Math.hypot(px - x0, py - y0);
  const t = Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / len2));
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy));
}

function isFlatCubic(
  x0: number, y0: number,
  x1: number, y1: number,
  x2: number, y2: number,
  x3: number, y3: number
): boolean {
  return pointLineDist(x1, y1, x0, y0, x3, y3) <= 0.85 && pointLineDist(x2, y2, x0, y0, x3, y3) <= 0.85;
}

function snapToAxis(p0: Pt, p1: Pt): AxisSegment | null {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  if (Math.abs(dx) <= AA_EPS || Math.abs(dx) / len <= AA_SLOPE) {
    const x = (p0.x + p1.x) / 2;
    return { axis: 'v', x0: x, x1: x, y0: Math.min(p0.y, p1.y), y1: Math.max(p0.y, p1.y) };
  }
  if (Math.abs(dy) <= AA_EPS || Math.abs(dy) / len <= AA_SLOPE) {
    const y = (p0.y + p1.y) / 2;
    return { axis: 'h', x0: Math.min(p0.x, p1.x), x1: Math.max(p0.x, p1.x), y0: y, y1: y };
  }
  return null;
}

const PATH_TOKEN = /([MmLlHhVvCcSsQqTtAaZz])|([+-]?(?:\d*\.\d+|\d+\.?)(?:[eE][+-]?\d+)?)/g;
const PARAM_COUNT: Record<string, number> = {
  m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0,
};

/**
 * Parse an SVG path `d` into axis-aligned line segments in the path's local space.
 * Nearly-straight cubics/quads that land on an axis are kept; glyph-like curves are dropped.
 */
export function parseSvgPathToAaSegments(d: string, aaEps = AA_EPS): AxisSegment[] {
  if (!d || !d.trim()) return [];
  const tokens: Array<string | number> = [];
  const re = new RegExp(PATH_TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) {
    if (m[1]) tokens.push(m[1]);
    else tokens.push(parseFloat(m[2]));
  }

  const segs: AxisSegment[] = [];
  let i = 0;
  let cx = 0;
  let cy = 0;
  let sx = 0;
  let sy = 0;
  let lastCmd = '';
  let prevCx = 0;
  let prevCy = 0;
  let prevQx = 0;
  let prevQy = 0;

  const emit = (x0: number, y0: number, x1: number, y1: number) => {
    const snapped = snapToAxis({ x: x0, y: y0 }, { x: x1, y: y1 });
    if (snapped) segs.push(snapped);
  };

  const read = (n: number): number[] | null => {
    const out: number[] = [];
    for (let k = 0; k < n; k++) {
      if (i >= tokens.length || typeof tokens[i] !== 'number') return null;
      out.push(tokens[i] as number);
      i += 1;
    }
    return out;
  };

  while (i < tokens.length) {
    let cmd: string;
    if (typeof tokens[i] === 'string') {
      cmd = tokens[i] as string;
      i += 1;
    } else if (lastCmd) {
      cmd = lastCmd === 'M' ? 'L' : lastCmd === 'm' ? 'l' : lastCmd;
    } else {
      i += 1;
      continue;
    }

    const lc = cmd.toLowerCase();
    const rel = cmd === lc;
    const argc = PARAM_COUNT[lc];
    if (argc == null) {
      lastCmd = cmd;
      continue;
    }
    if (lc === 'z') {
      emit(cx, cy, sx, sy);
      cx = sx;
      cy = sy;
      lastCmd = cmd;
      continue;
    }

    const args = read(argc);
    if (!args) break;

    if (lc === 'm') {
      const x = rel ? cx + args[0] : args[0];
      const y = rel ? cy + args[1] : args[1];
      cx = x;
      cy = y;
      sx = x;
      sy = y;
      lastCmd = cmd;
      continue;
    }

    if (lc === 'l') {
      const x = rel ? cx + args[0] : args[0];
      const y = rel ? cy + args[1] : args[1];
      emit(cx, cy, x, y);
      cx = x;
      cy = y;
    } else if (lc === 'h') {
      const x = rel ? cx + args[0] : args[0];
      emit(cx, cy, x, cy);
      cx = x;
    } else if (lc === 'v') {
      const y = rel ? cy + args[0] : args[0];
      emit(cx, cy, cx, y);
      cy = y;
    } else if (lc === 'c') {
      const x1 = rel ? cx + args[0] : args[0];
      const y1 = rel ? cy + args[1] : args[1];
      const x2 = rel ? cx + args[2] : args[2];
      const y2 = rel ? cy + args[3] : args[3];
      const x = rel ? cx + args[4] : args[4];
      const y = rel ? cy + args[5] : args[5];
      if (isFlatCubic(cx, cy, x1, y1, x2, y2, x, y)) emit(cx, cy, x, y);
      prevCx = x2;
      prevCy = y2;
      cx = x;
      cy = y;
    } else if (lc === 's') {
      const x2 = rel ? cx + args[0] : args[0];
      const y2 = rel ? cy + args[1] : args[1];
      const x = rel ? cx + args[2] : args[2];
      const y = rel ? cy + args[3] : args[3];
      const x1 = lastCmd.toLowerCase() === 'c' || lastCmd.toLowerCase() === 's' ? 2 * cx - prevCx : cx;
      const y1 = lastCmd.toLowerCase() === 'c' || lastCmd.toLowerCase() === 's' ? 2 * cy - prevCy : cy;
      if (isFlatCubic(cx, cy, x1, y1, x2, y2, x, y)) emit(cx, cy, x, y);
      prevCx = x2;
      prevCy = y2;
      cx = x;
      cy = y;
    } else if (lc === 'q') {
      const x1 = rel ? cx + args[0] : args[0];
      const y1 = rel ? cy + args[1] : args[1];
      const x = rel ? cx + args[2] : args[2];
      const y = rel ? cy + args[3] : args[3];
      if (pointLineDist(x1, y1, cx, cy, x, y) <= 0.85) emit(cx, cy, x, y);
      prevQx = x1;
      prevQy = y1;
      cx = x;
      cy = y;
    } else if (lc === 't') {
      const x = rel ? cx + args[0] : args[0];
      const y = rel ? cy + args[1] : args[1];
      const x1 = lastCmd.toLowerCase() === 'q' || lastCmd.toLowerCase() === 't' ? 2 * cx - prevQx : cx;
      const y1 = lastCmd.toLowerCase() === 'q' || lastCmd.toLowerCase() === 't' ? 2 * cy - prevQy : cy;
      if (pointLineDist(x1, y1, cx, cy, x, y) <= 0.85) emit(cx, cy, x, y);
      prevQx = x1;
      prevQy = y1;
      cx = x;
      cy = y;
    } else if (lc === 'a') {
      const x = rel ? cx + args[5] : args[5];
      const y = rel ? cy + args[6] : args[6];
      if (Math.abs(args[0]) <= aaEps && Math.abs(args[1]) <= aaEps) emit(cx, cy, x, y);
      cx = x;
      cy = y;
    }

    lastCmd = cmd;
  }

  return segs;
}

function parsePointsAttr(points: string | null): Pt[] {
  if (!points) return [];
  const n = parseNums(points);
  const pts: Pt[] = [];
  for (let i = 0; i + 1 < n.length; i += 2) pts.push({ x: n[i], y: n[i + 1] });
  return pts;
}

export function clusterWeightedGuides(
  values: WeightedGuide[],
  eps = CLUSTER_EPS,
  maxSpan = CLUSTER_MAX_SPAN
): number[] {
  const sorted = values
    .filter(v => Number.isFinite(v.value) && Number.isFinite(v.weight) && v.weight > 0)
    .sort((a, b) => a.value - b.value);
  if (sorted.length === 0) return [];

  const out: number[] = [];
  let sumVW = sorted[0].value * sorted[0].weight;
  let sumW = sorted[0].weight;
  let min = sorted[0].value;
  let last = sorted[0].value;

  const flush = () => {
    if (sumW > 0) out.push(sumVW / sumW);
  };

  for (let i = 1; i < sorted.length; i++) {
    const v = sorted[i].value;
    const w = sorted[i].weight;
    if (v - last <= eps && v - min <= maxSpan) {
      sumVW += v * w;
      sumW += w;
      last = v;
    } else {
      flush();
      sumVW = v * w;
      sumW = w;
      min = v;
      last = v;
    }
  }
  flush();
  return out;
}

function barFromSegment(seg: AxisSegment, half: number): SimpleRect {
  if (seg.axis === 'v') {
    return {
      left: seg.x0 - half,
      right: seg.x0 + half,
      top: Math.min(seg.y0, seg.y1),
      bottom: Math.max(seg.y0, seg.y1),
    };
  }
  return {
    left: Math.min(seg.x0, seg.x1),
    right: Math.max(seg.x0, seg.x1),
    top: seg.y0 - half,
    bottom: seg.y0 + half,
  };
}

function normalizeRect(r: SimpleRect): SimpleRect | null {
  const left = Math.min(r.left, r.right);
  const right = Math.max(r.left, r.right);
  const top = Math.min(r.top, r.bottom);
  const bottom = Math.max(r.top, r.bottom);
  if (!(right > left) || !(bottom > top)) {
    if (Math.abs(right - left) < 0.05 && bottom - top >= 1) {
      return { left: left - 0.4, right: left + 0.4, top, bottom };
    }
    if (Math.abs(bottom - top) < 0.05 && right - left >= 1) {
      return { left, right, top: top - 0.4, bottom: top + 0.4 };
    }
    return null;
  }
  return { left, right, top, bottom };
}

function isVerticalBar(r: SimpleRect): boolean {
  return (r.right - r.left) <= (r.bottom - r.top);
}

function mergeCollinearBars(bars: SimpleRect[]): SimpleRect[] {
  const mergeRun = (items: SimpleRect[], vertical: boolean): SimpleRect[] => {
    if (items.length <= 1) return items;
    const sorted = items.slice().sort((a, b) => (vertical ? a.top - b.top : a.left - b.left));
    const out: SimpleRect[] = [];
    let cur = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
      const n = sorted[i];
      if (vertical) {
        const gap = n.top - cur.bottom;
        const tClose = Math.abs((n.right - n.left) - (cur.right - cur.left)) <= 1.5;
        if (tClose && gap <= MERGE_GAP) {
          cur.top = Math.min(cur.top, n.top);
          cur.bottom = Math.max(cur.bottom, n.bottom);
          cur.left = (cur.left + n.left) / 2;
          cur.right = (cur.right + n.right) / 2;
          continue;
        }
      } else {
        const gap = n.left - cur.right;
        const tClose = Math.abs((n.bottom - n.top) - (cur.bottom - cur.top)) <= 1.5;
        if (tClose && gap <= MERGE_GAP) {
          cur.left = Math.min(cur.left, n.left);
          cur.right = Math.max(cur.right, n.right);
          cur.top = (cur.top + n.top) / 2;
          cur.bottom = (cur.bottom + n.bottom) / 2;
          continue;
        }
      }
      out.push(cur);
      cur = { ...n };
    }
    out.push(cur);
    return out;
  };

  const groupKey = (v: number) => Math.round(v * 2) / 2;
  const mergeByLane = (items: SimpleRect[], vertical: boolean): SimpleRect[] => {
    const lanes = new Map<number, SimpleRect[]>();
    items.forEach(r => {
      const key = vertical ? groupKey((r.left + r.right) / 2) : groupKey((r.top + r.bottom) / 2);
      const list = lanes.get(key);
      if (list) list.push(r);
      else lanes.set(key, [r]);
    });
    const out: SimpleRect[] = [];
    lanes.forEach(list => out.push(...mergeRun(list, vertical)));
    return out;
  };

  const rules = bars.filter(r => Math.min(r.right - r.left, r.bottom - r.top) <= MAX_RULE_THICKNESS);
  const cells = bars.filter(r => Math.min(r.right - r.left, r.bottom - r.top) > MAX_RULE_THICKNESS);
  const v = mergeByLane(rules.filter(isVerticalBar), true);
  const h = mergeByLane(rules.filter(r => !isVerticalBar(r)), false);
  return [...v, ...h, ...cells];
}

function guidesFromBars(bars: SimpleRect[], minLen: number): Guides {
  const xs: WeightedGuide[] = [];
  const ys: WeightedGuide[] = [];

  const pushX = (r: SimpleRect, thick: number, h: number) => {
    if (thick <= STROKE_CENTER_MAX) xs.push({ value: (r.left + r.right) / 2, weight: h });
    else {
      xs.push({ value: r.left, weight: h });
      xs.push({ value: r.right, weight: h });
    }
  };
  const pushY = (r: SimpleRect, thick: number, w: number) => {
    if (thick <= STROKE_CENTER_MAX) ys.push({ value: (r.top + r.bottom) / 2, weight: w });
    else {
      ys.push({ value: r.top, weight: w });
      ys.push({ value: r.bottom, weight: w });
    }
  };

  bars.forEach(r => {
    const w = r.right - r.left;
    const h = r.bottom - r.top;
    const len = Math.max(w, h);
    if (len < minLen) return;
    const thick = Math.min(w, h);
    if (thick > MAX_RULE_THICKNESS) {
      pushX(r, thick, h);
      pushY(r, thick, w);
      return;
    }
    if (w <= h) pushX(r, thick, h);
    else pushY(r, thick, w);
  });

  const supported = (guides: number[], pick: 'x' | 'y') =>
    guides.filter(g => {
      const total = bars.reduce((s, r) => {
        const long = Math.max(r.right - r.left, r.bottom - r.top);
        if (long < minLen) return s;
        const thick = Math.min(r.right - r.left, r.bottom - r.top);
        const isCell = thick > MAX_RULE_THICKNESS;
        if (pick === 'x') {
          if (!isCell && !isVerticalBar(r)) return s;
          const near = Math.abs(r.left - g) <= 1.5 || Math.abs(r.right - g) <= 1.5 || Math.abs((r.left + r.right) / 2 - g) <= 1.5;
          return near ? s + (r.bottom - r.top) : s;
        }
        if (!isCell && isVerticalBar(r)) return s;
        const near = Math.abs(r.top - g) <= 1.5 || Math.abs(r.bottom - g) <= 1.5 || Math.abs((r.top + r.bottom) / 2 - g) <= 1.5;
        return near ? s + (r.right - r.left) : s;
      }, 0);
      return total >= minLen;
    });

  return {
    x: supported(clusterWeightedGuides(xs, GUIDE_COLLAPSE_PX, GUIDE_COLLAPSE_PX), 'x'),
    y: supported(clusterWeightedGuides(ys, GUIDE_COLLAPSE_PX, GUIDE_COLLAPSE_PX), 'y'),
  };
}

export function guidesFromAxisSegments(
  segments: AxisSegment[],
  opts: { pageWidth: number; pageHeight: number; strokeWidth?: number }
): { guides: Guides; supportRects: SimpleRect[] } {
  const half = Math.max(0.4, (opts.strokeWidth ?? 1) / 2);
  const bars = segments
    .map(seg => normalizeRect(barFromSegment(seg, half)))
    .filter((r): r is SimpleRect => !!r);
  const minLen = Math.max(MIN_SEG_LEN_PX, Math.min(opts.pageWidth, opts.pageHeight) * MIN_SEG_LEN_RATIO);
  const merged = mergeCollinearBars(bars);
  return { guides: guidesFromBars(merged, minLen), supportRects: merged };
}

type PointMapper = (el: SVGGraphicsElement, x: number, y: number) => Pt;

function makePointMapper(
  svgEl: SVGSVGElement,
  formRect: DOMRect,
  cssScale: number
): PointMapper {
  const scale = cssScale || 1;
  return (el, x, y) => {
    try {
      const ctm = el.getScreenCTM();
      if (ctm) {
        return {
          x: (ctm.a * x + ctm.c * y + ctm.e - formRect.left) / scale,
          y: (ctm.b * x + ctm.d * y + ctm.f - formRect.top) / scale,
        };
      }
    } catch {}
    return applyMat(matrixFromSvgTree(el, svgEl), x, y);
  };
}

function strokeHalfInFormSpace(el: SVGGraphicsElement, map: PointMapper, x: number, y: number): number {
  const sw = strokeWidthLocal(el);
  const a = map(el, x, y);
  const b = map(el, x + sw, y);
  const c = map(el, x, y + sw);
  return Math.max(0.4, Math.min(Math.hypot(b.x - a.x, b.y - a.y), Math.hypot(c.x - a.x, c.y - a.y)) / 2);
}

function mapSegment(seg: AxisSegment, el: SVGGraphicsElement, map: PointMapper): AxisSegment | null {
  const p0 = map(el, seg.x0, seg.y0);
  const p1 = map(el, seg.x1, seg.y1);
  return snapToAxis(p0, p1);
}

function rectCornersToBar(
  el: SVGGraphicsElement,
  map: PointMapper,
  x: number,
  y: number,
  w: number,
  h: number,
  includeStroke: boolean,
  pageW: number,
  pageH: number
): SimpleRect[] {
  if (w <= 0 || h <= 0) return [];
  const pts = [
    map(el, x, y),
    map(el, x + w, y),
    map(el, x + w, y + h),
    map(el, x, y + h),
  ];
  const xs = pts.map(p => p.x);
  const ys = pts.map(p => p.y);
  let left = Math.min(...xs);
  let right = Math.max(...xs);
  let top = Math.min(...ys);
  let bottom = Math.max(...ys);
  // Drop rotated parallelograms that are no longer axis-aligned
  const uniqueX = xs.filter((v, i, a) => a.findIndex(u => Math.abs(u - v) < 1.2) === i);
  const uniqueY = ys.filter((v, i, a) => a.findIndex(u => Math.abs(u - v) < 1.2) === i);
  if (uniqueX.length > 2 || uniqueY.length > 2) return [];

  if (includeStroke) {
    const half = strokeHalfInFormSpace(el, map, x, y);
    left -= half;
    right += half;
    top -= half;
    bottom += half;
  }

  const box = normalizeRect({ left, right, top, bottom });
  if (!box) return [];
  const bw = box.right - box.left;
  const bh = box.bottom - box.top;
  const area = bw * bh;
  if (area > MAX_CELL_AREA_RATIO * pageW * pageH) return [];

  const thick = Math.min(bw, bh);
  if (thick <= MAX_RULE_THICKNESS) return [box];

  // Cell / filled region: keep the box so both inner edges are available
  return [box];
}

function collectBarsFromElement(
  el: SVGGraphicsElement,
  map: PointMapper,
  pageW: number,
  pageH: number,
  bars: SimpleRect[]
) {
  const tag = el.tagName.toLowerCase().replace(/^svg:/, '');
  const fill = attrOrCss(el, 'fill', 'fill');
  const stroke = attrOrCss(el, 'stroke', 'stroke');
  const hasStroke = !isPaintNone(stroke);
  // Line art from pdf2svg is usually stroke-only. Only assume SVG's default
  // black fill when the element has no stroke and no explicit fill.
  const hasFill = fill
    ? !isPaintNone(fill)
    : !hasStroke && tag !== 'line' && tag !== 'polyline';

  const pushSegs = (local: AxisSegment[], half: number) => {
    local.forEach(seg => {
      const mapped = mapSegment(seg, el, map);
      if (!mapped) return;
      if (Math.max(mapped.x1 - mapped.x0, mapped.y1 - mapped.y0) < 1) return;
      const bar = normalizeRect(barFromSegment(mapped, half));
      if (bar) bars.push(bar);
    });
  };

  if (tag === 'line') {
    if (!hasStroke && !hasFill) return;
    const x1 = numAttr(el, 'x1');
    const y1 = numAttr(el, 'y1');
    const x2 = numAttr(el, 'x2');
    const y2 = numAttr(el, 'y2');
    const snapped = snapToAxis(map(el, x1, y1), map(el, x2, y2));
    if (!snapped) return;
    const half = hasStroke ? strokeHalfInFormSpace(el, map, x1, y1) : 0.4;
    const bar = normalizeRect(barFromSegment(snapped, half));
    if (bar) bars.push(bar);
    return;
  }

  if (tag === 'rect') {
    if (!hasStroke && !hasFill) return;
    const x = numAttr(el, 'x');
    const y = numAttr(el, 'y');
    const w = numAttr(el, 'width');
    const h = numAttr(el, 'height');
    bars.push(...rectCornersToBar(el, map, x, y, w, h, hasStroke, pageW, pageH));
    return;
  }

  if (tag === 'polyline' || tag === 'polygon') {
    const pts = parsePointsAttr(el.getAttribute('points'));
    if (pts.length < 2) return;
    const local: AxisSegment[] = [];
    const count = tag === 'polygon' ? pts.length : pts.length - 1;
    for (let i = 0; i < count; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      const s = snapToAxis(a, b);
      if (s) local.push(s);
    }
    const half = hasStroke ? strokeHalfInFormSpace(el, map, pts[0].x, pts[0].y) : 0.4;
    pushSegs(local, half);
    return;
  }

  if (tag === 'path') {
    const d = el.getAttribute('d') || '';
    if (!d) return;
    if (looksLikeGlyphOutline(d)) return;
    const local = parseSvgPathToAaSegments(d);
    if (local.length === 0) return;

    // Closed rectangular fill (a cell) in addition to individual strokes
    if (hasFill && local.length >= 4) {
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      local.forEach(s => {
        minX = Math.min(minX, s.x0, s.x1);
        maxX = Math.max(maxX, s.x0, s.x1);
        minY = Math.min(minY, s.y0, s.y1);
        maxY = Math.max(maxY, s.y0, s.y1);
      });
      const lw = maxX - minX;
      const lh = maxY - minY;
      if (lw > MAX_RULE_THICKNESS && lh > MAX_RULE_THICKNESS) {
        bars.push(...rectCornersToBar(el, map, minX, minY, lw, lh, false, pageW, pageH));
      }
    }

    const half = hasStroke ? strokeHalfInFormSpace(el, map, local[0].x0, local[0].y0) : 0.4;
    pushSegs(local, hasStroke || !hasFill ? half : 0.4);
  }
}

function extractSvgGeometry(
  svgEl: SVGSVGElement | null | undefined,
  formEl: HTMLElement | null | undefined
): { guides: Guides; supportRects: SimpleRect[] } {
  if (!svgEl || !formEl) return { guides: { x: [], y: [] }, supportRects: [] };

  const pageW = formEl.offsetWidth || formEl.clientWidth || 1;
  const pageH = formEl.offsetHeight || formEl.clientHeight || 1;
  const cssScale = readCssScale(formEl) || 1;
  const formRect = formEl.getBoundingClientRect();
  const map = makePointMapper(svgEl, formRect, cssScale);

  const bars: SimpleRect[] = [];
  svgEl.querySelectorAll('line, rect, path, polyline, polygon').forEach((node) => {
    if (shouldSkipElement(node)) return;
    collectBarsFromElement(node as SVGGraphicsElement, map, pageW, pageH, bars);
  });

  const minLen = Math.max(MIN_SEG_LEN_PX, Math.min(pageW, pageH) * MIN_SEG_LEN_RATIO);
  const merged = mergeCollinearBars(bars);
  return { guides: guidesFromBars(merged, minLen), supportRects: merged };
}

export function excludeSuppressedGuides(
  guides: Guides,
  suppressed: { x?: number[]; y?: number[] } | null | undefined,
  eps = 1.5
): Guides {
  const skip = (v: number, list: number[] | undefined) =>
    (list || []).some(s => Math.abs(s - v) <= eps);
  return {
    x: (guides.x || []).filter(v => !skip(v, suppressed?.x)),
    y: (guides.y || []).filter(v => !skip(v, suppressed?.y)),
  };
}

export function buildSvgGuides(
  svgEl: SVGSVGElement | null | undefined,
  formEl: HTMLElement | null | undefined
): Guides {
  return extractSvgGeometry(svgEl, formEl).guides;
}

export function buildSvgGuidesWithSupportRects(
  svgEl: SVGSVGElement | null | undefined,
  formEl: HTMLElement | null | undefined
): { guides: Guides; supportRects: SimpleRect[] } {
  return extractSvgGeometry(svgEl, formEl);
}
